import React, { useEffect, useState, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { scheduleUndoableDelete } from "@/lib/undoDelete";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Plus, ClipboardText } from "@phosphor-icons/react";
import SoAdjustmentModal from "@/components/SoAdjustmentModal";
import LowStockAlertPanel from "./salesOrders/LowStockAlertPanel";
import SalesOrderFilters from "./salesOrders/SalesOrderFilters";
import SalesOrdersTable from "./salesOrders/SalesOrdersTable";
import SalesOrderFormDialog from "./salesOrders/SalesOrderFormDialog";

const EMPTY_FORM = {
  no_so: "", plant_id: "", produk_id: "", distrik_id: "",
  kuantiti_total: "",
  tanggal_terbit: new Date().toISOString().slice(0, 10),
  aktif: true,
};

export default function SalesOrders() {
  const [items, setItems] = useState([]);
  const [adjustingSo, setAdjustingSo] = useState(null);
  const [plants, setPlants] = useState([]);
  const [products, setProducts] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [filterDistrik, setFilterDistrik] = useState("__all__");
  const [q, setQ] = useState("");
  const [sortMode, setSortMode] = useState("date_desc");
  const [showExhausted, setShowExhausted] = useState(false);
  const [showExpired, setShowExpired] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const [so, pl, pr, ds, al] = await Promise.all([
        api.get("/sales-orders"),
        api.get("/plants", { params: { aktif: true } }),
        api.get("/products", { params: { aktif: true } }),
        api.get("/districts", { params: { aktif: true } }),
        api.get("/products/alerts"),
      ]);
      setItems(so.data);
      setPlants(pl.data);
      setProducts(pr.data);
      setDistricts(ds.data);
      setAlerts(al.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM, tanggal_terbit: new Date().toISOString().slice(0, 10) });
    setOpen(true);
  };

  const openEdit = (so) => {
    setEditing(so);
    setForm({
      no_so: so.no_so,
      plant_id: so.plant_id,
      produk_id: so.produk_id,
      distrik_id: so.distrik_id || "",
      kuantiti_total: String(so.kuantiti_total),
      tanggal_terbit: so.tanggal_terbit || "",
      aktif: so.aktif,
    });
    setOpen(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.no_so.trim() || !form.plant_id || !form.produk_id || !form.kuantiti_total) {
      toast.error("Lengkapi semua field wajib");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        no_so: form.no_so.trim(),
        plant_id: form.plant_id,
        produk_id: form.produk_id,
        distrik_id: form.distrik_id || null,
        kuantiti_total: parseFloat(form.kuantiti_total),
        tanggal_terbit: form.tanggal_terbit || null,
        aktif: form.aktif,
      };
      if (editing) {
        await api.put(`/sales-orders/${editing.id}`, payload);
        toast.success("SO diupdate");
      } else {
        await api.post("/sales-orders", payload);
        toast.success("SO ditambahkan");
      }
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  const onDelete = (so) => {
    scheduleUndoableDelete({
      items, setItems, target: so,
      label: `SO ${so.no_so}`,
      // No `force=true`: backend auto-soft-deletes when the SO has been
      // consumed by an SPB. Hard-delete would orphan those SPB references
      // and break restore-cancelled flow.
      apiCall: () => api.delete(`/sales-orders/${so.id}`),
      onDone: load,
    });
  };

  const filteredItems = useMemo(() => {
    // 1) Distrik filter
    let filtered = filterDistrik === "__all__"
      ? items
      : filterDistrik === "__none__"
        ? items.filter((so) => !so.distrik_id)
        : items.filter((so) => so.distrik_id === filterDistrik);
    // 2) Text search — matches No SO / Plant / Produk / Distrik (case-insensitive)
    const needle = q.trim().toLowerCase();
    if (needle) {
      const plantName = (id) => (plants.find((p) => p.id === id)?.nama || "").toLowerCase();
      const productName = (id) => (products.find((p) => p.id === id)?.nama || "").toLowerCase();
      const districtName = (id) => {
        const d = districts.find((x) => x.id === id);
        return d ? `${d.kode} ${d.nama}`.toLowerCase() : "";
      };
      filtered = filtered.filter((so) => {
        const hay = [
          so.no_so || "",
          plantName(so.plant_id),
          productName(so.produk_id),
          districtName(so.distrik_id),
        ].join(" ").toLowerCase();
        return hay.includes(needle);
      });
    }
    // 3) Hide exhausted (sisa <= 0) unless toggled on
    if (!showExhausted) filtered = filtered.filter((so) => Number(so.kuantiti_sisa) > 0);
    // 4) Hide expired unless toggled on
    if (!showExpired) filtered = filtered.filter((so) => !so.is_expired);
    // 5) Sort
    const arr = [...filtered];
    const plantLabel = (id) => (plants.find((p) => p.id === id)?.nama || "").toLowerCase();
    const productLabel = (id) => (products.find((p) => p.id === id)?.nama || "").toLowerCase();
    const cmp = (a, b) => (a || "").localeCompare(b || "");
    const byDate = (a, b) => cmp(b.tanggal_terbit, a.tanggal_terbit);
    const sortFns = {
      date_desc: (a, b) => cmp(b.tanggal_terbit, a.tanggal_terbit),
      date_asc: (a, b) => cmp(a.tanggal_terbit, b.tanggal_terbit),
      no_so_asc: (a, b) => cmp(a.no_so, b.no_so),
      no_so_desc: (a, b) => cmp(b.no_so, a.no_so),
      plant_asc: (a, b) => cmp(plantLabel(a.plant_id), plantLabel(b.plant_id)) || byDate(a, b),
      plant_desc: (a, b) => cmp(plantLabel(b.plant_id), plantLabel(a.plant_id)) || byDate(a, b),
      produk_asc: (a, b) => cmp(productLabel(a.produk_id), productLabel(b.produk_id)) || byDate(a, b),
      produk_desc: (a, b) => cmp(productLabel(b.produk_id), productLabel(a.produk_id)) || byDate(a, b),
    };
    const fn = sortFns[sortMode];
    if (fn) arr.sort(fn);
    return arr;
  }, [items, filterDistrik, q, sortMode, showExhausted, showExpired, plants, products, districts]);

  const hiddenCounts = useMemo(() => {
    const base = filterDistrik === "__all__"
      ? items
      : filterDistrik === "__none__"
        ? items.filter((so) => !so.distrik_id)
        : items.filter((so) => so.distrik_id === filterDistrik);
    return {
      exhausted: base.filter((so) => Number(so.kuantiti_sisa) <= 0 && !so.is_expired).length,
      expired: base.filter((so) => so.is_expired).length,
    };
  }, [items, filterDistrik]);

  const toggleSort = (col) => {
    setSortMode((m) => {
      if (col === "date") return m === "date_desc" ? "date_asc" : "date_desc";
      if (col === "no_so") return m === "no_so_asc" ? "no_so_desc" : "no_so_asc";
      if (col === "plant") return m === "plant_asc" ? "plant_desc" : "plant_asc";
      if (col === "produk") return m === "produk_asc" ? "produk_desc" : "produk_asc";
      return m;
    });
  };

  return (
    <div className="p-6 md:p-8 max-w-6xl">
      <div className="flex flex-col md:flex-row md:items-end justify-between border-b border-border pb-6 mb-6 gap-4">
        <div>
          <div className="overline">SPB</div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight mt-2 flex items-center gap-3">
            <span className="w-11 h-11 rounded-full bg-orange-100 text-primary flex items-center justify-center shrink-0">
              <ClipboardText size={22} weight="bold" />
            </span>
            Sales Order (SO)
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Input No SO dari pabrik per produk beserta kuantitasnya. SPB akan otomatis pakai FIFO + auto-split.
          </p>
        </div>
        <Button
          onClick={openNew}
          className="rounded-lg font-bold uppercase tracking-wider"
          data-testid="add-so-button"
        >
          <Plus size={16} className="mr-2" /> Tambah SO
        </Button>
      </div>

      <LowStockAlertPanel alerts={alerts} />

      <SalesOrderFilters
        q={q}
        onQChange={setQ}
        filterDistrik={filterDistrik}
        onFilterDistrikChange={setFilterDistrik}
        districts={districts}
        showExhausted={showExhausted}
        onShowExhaustedChange={setShowExhausted}
        showExpired={showExpired}
        onShowExpiredChange={setShowExpired}
        hiddenCounts={hiddenCounts}
        filteredCount={filteredItems.length}
        totalCount={items.length}
      />

      <SalesOrdersTable
        items={filteredItems}
        totalRawCount={items.length}
        alerts={alerts}
        plants={plants}
        products={products}
        districts={districts}
        sortMode={sortMode}
        onToggleSort={toggleSort}
        onEdit={openEdit}
        onDelete={onDelete}
        onAdjust={setAdjustingSo}
      />

      <SalesOrderFormDialog
        open={open}
        onOpenChange={setOpen}
        editing={editing}
        form={form}
        setForm={setForm}
        submitting={submitting}
        onSubmit={onSubmit}
        plants={plants}
        products={products}
        districts={districts}
      />

      <SoAdjustmentModal
        so={adjustingSo}
        open={!!adjustingSo}
        onClose={() => setAdjustingSo(null)}
        onApplied={load}
      />
    </div>
  );
}
